import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Marca de agua camaleón semitransparente — centro fijo detrás del contenido
function ChameleonWatermark() {
  const { theme } = useTheme();
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(480px, 60vw)',
        height: 'min(480px, 60vw)',
        backgroundImage: `url(${process.env.PUBLIC_URL}/logo192.png)`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        opacity: theme === 'light' ? 0.06 : 0.03,
        pointerEvents: 'none',
        zIndex: 0,
        filter: theme === 'light' ? 'saturate(0.4)' : 'grayscale(0.2)',
        transition: 'opacity 0.3s ease',
      }}
    />
  );
}
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Store from './pages/Store';
import ProductDetail from './pages/ProductDetail';
import BoosterDetail from './pages/BoosterDetail';
import Cart from './pages/Cart';
import CheckoutSuccess from './pages/CheckoutSuccess';
import GameReservation from './pages/GameReservation';
import TableReservation from './pages/TableReservation';
import Events from './pages/Events';
import Tournaments from './pages/Tournaments';
import EventDetail from './pages/EventDetail';
import Collections from './pages/Collections';
import CardCatalog from './pages/CardCatalog';
import Loyalty from './pages/Loyalty';
import Wishlist from './pages/Wishlist';
import Profile from './pages/Profile';
import ControlPanel from './pages/ControlPanel';
import './index.css';

function Layout() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
    </div>
  );
  if (user === false) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Callback Google OAuth: detectar session_id en el fragmento de la URL de forma síncrona
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="store" element={<Store />} />
        <Route path="store/:productId" element={<ProductDetail />} />
        <Route path="boosters/:boosterId" element={<BoosterDetail />} />
        <Route path="cart" element={<Cart />} />
        <Route path="checkout/success" element={<CheckoutSuccess />} />
        <Route path="reservations/games" element={<GameReservation />} />
        <Route path="reservations/tables" element={<ProtectedRoute><TableReservation /></ProtectedRoute>} />
        <Route path="events" element={<Events />} />
        <Route path="torneos" element={<Tournaments />} />
        <Route path="events/:eventId" element={<EventDetail />} />
        <Route path="leaderboard" element={<Navigate to="/torneos?view=ranking" replace />} />
        <Route path="collections" element={<Collections />} />
        <Route path="cards" element={<CardCatalog />} />
        <Route path="loyalty" element={<ProtectedRoute><Loyalty /></ProtectedRoute>} />
        <Route path="wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
        <Route path="profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="admin" element={<ProtectedRoute><ControlPanel /></ProtectedRoute>} />
      </Route>
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <ChameleonWatermark />
              <AppRouter />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
